/** Visual style presets for narration “Create story via prompt” (keys match backend). */

import api from '@/lib/api';

export const DEFAULT_NARRATION_STORYBOARD_STYLE = 'pixar_cinematic';

/** Must match backend `MAX_IMAGE_STORY_PANEL_COUNT` in narration_storyboard_styles.py */
export const MAX_IMAGE_STORY_PANEL_COUNT = 24;

/** Art-only: color, medium, lighting, texture. Plot/subject rules are applied separately on the server. */
export const NARRATION_STORYBOARD_STYLES = [
  {
    value: 'web_scraper',
    label: 'Web scraper (topic images)',
    directive:
      'Real web-discovered photos from your image search prompt — no AI-generated art style.',
  },
  {
    value: 'pixar_cinematic',
    label: 'Pixar / Toy Story',
    directive:
      '3D-inspired digital illustration, rounded forms, warm golden-hour light, soft shadows, shallow depth of field, saturated naturalistic color, smooth gradients, expressive eyes.',
  },
  {
    value: 'anime',
    label: 'Anime',
    directive:
      'Anime cel-shaded look, crisp ink outlines, flat fills with sharp highlights, vivid saturated palette, strong backlight, clean gradient skies.',
  },
  {
    value: 'studio_ghibli',
    label: 'Studio Ghibli',
    directive:
      'Watercolor-gouache painted look, soft natural light, pastel skies, paper grain, muted earthy palette, gentle linework, soft edges.',
  },
  {
    value: 'photoreal_cinematic',
    label: 'Photoreal / cinematic real',
    directive:
      'Photoreal live-action still, natural skin texture, lens depth of field, practical lighting, neutral-warm grade, subtle film grain, background bokeh.',
  },
  {
    value: 'hyperrealistic',
    label: 'Hyperrealistic',
    directive:
      'Ultra-realistic mythological fantasy (UE5/Octane AAA cinematic), dark temples/caves/Naglok, massive scale, god-like heroes, volumetric god rays, teal+gold grade, 8K photoreal concept art — not cartoon or anime.',
  },
  {
    value: 'indian_political_satire',
    label: 'Satirical photoreal (news-comedy look)',
    directive:
      'Semi-photoreal satirical look, slight caricature, punchy contrast, warm mixed lighting, saturated primaries, crisp digital sharpness, bold silhouettes.',
  },
  {
    value: 'classic_disney',
    label: 'Classic Disney animation',
    directive:
      '2D hand-drawn cel animation, warm ink lines, flat fills, soft watercolor backgrounds, storybook primaries and pastels.',
  },
  {
    value: 'comic_book',
    label: 'Comic book',
    directive:
      'Comic-book print look, heavy inks, halftone dots, high contrast, bold color blocking, graphic shadow shapes.',
  },
  {
    value: 'manga_seinen',
    label: 'Manga (seinen)',
    directive:
      'Seinen manga monochrome, fine linework, screentone texture, high contrast grayscale, detailed hatching.',
  },
  {
    value: 'watercolor_storybook',
    label: 'Watercolor storybook',
    directive:
      'Watercolor illustration, soft washes, bleeding edges, paper texture, pastel palette, low contrast, wet-on-wet gradients.',
  },
  {
    value: 'digital_painting',
    label: 'Digital painting',
    directive:
      'Painterly digital illustration, visible brushstrokes, layered glazing, atmospheric depth, cinematic rim light, concept-art texture.',
  },
  {
    value: 'claymation',
    label: 'Claymation / stop-motion',
    directive:
      'Clay stop-motion look, matte sculpted surfaces, tool marks, warm tungsten light, shallow macro focus, earthy palette.',
  },
  {
    value: 'oil_painting',
    label: 'Classical oil painting',
    directive:
      'Oil-on-canvas look, impasto strokes, chiaroscuro, earth-tone palette, warm glaze highlights, baroque composition.',
  },
  {
    value: 'noir_film',
    label: 'Film noir',
    directive:
      'Noir monochrome still, high contrast, hard key light, stripe shadows, smoke haze, wet highlights, film grain.',
  },
  {
    value: 'cyberpunk_neon',
    label: 'Cyberpunk neon',
    directive:
      'Neon-noir sci-fi illustration, magenta-cyan rim light, wet speculars, neon bloom, dark high-contrast night palette.',
  },
  {
    value: 'minimalist_flat',
    label: 'Minimalist flat',
    directive:
      'Flat vector illustration, hard geometric edges, limited palette, flat fills, bold silhouettes, generous negative space.',
  },
  {
    value: 'dreamlike_surreal',
    label: 'Dreamlike surreal',
    directive:
      'Surrealist painterly look, ethereal glow, pearlescent highlights, pastel-iridescent gradients, soft blended edges, dream haze.',
  },
  {
    value: 'retro_80s_cartoon',
    label: 'Retro 80s cartoon',
    directive:
      '1980s cel cartoon frame, bold outlines, flat saturated primaries, simple two-tone shading, slight VHS softness.',
  },
];

export function storyboardStyleMeta(styleKey) {
  return NARRATION_STORYBOARD_STYLES.find((s) => s.value === styleKey)
    || NARRATION_STORYBOARD_STYLES.find((s) => s.value === DEFAULT_NARRATION_STORYBOARD_STYLE);
}

const BUSINESS_B2B_TERMS = [
  'salesperson', 'sales person', 'sales team', 'b2b', 'industrial', 'factory', 'manufacturing',
  'cement plant', 'steel mill', 'plant manager', 'lubricant', 'machinery', 'uptime', 'reliability',
  'customer', 'vendor', 'premium me', 'production target', 'downtime', 'well-dressed',
];

export function plotSuggestsBusinessB2B(plot) {
  const low = String(plot || '').toLowerCase();
  return BUSINESS_B2B_TERMS.filter((t) => low.includes(t)).length >= 2;
}

export function plotSuggestsIndianPoliticalSatire(plot) {
  const low = String(plot || '').toLowerCase();
  const strong = [
    'modi', 'bjp', 'congress party', 'political party', 'election', 'rally', 'parliament',
    'prime minister', 'campaign', 'political satire', 'news parody',
  ];
  if (strong.some((t) => low.includes(t))) return true;
  return (
    (low.includes('india') || low.includes('indian'))
    && /satire|comedy|news parody/.test(low)
  );
}

/**
 * Fetches the collage image-generation prompt preview from the backend
 * (`build_storyboard_collage_image_prompt` in narration_storyboard_styles.py).
 */
export async function buildImageGenerationPromptPreview({
  panelCount,
  aspectRatio,
  visualStyleKey,
  plotText,
  panels = null,
}) {
  const body = {
    plot: plotText || '',
    panel_count: Math.min(MAX_IMAGE_STORY_PANEL_COUNT, Math.max(1, Number(panelCount) || 1)),
    visual_style: visualStyleKey || DEFAULT_NARRATION_STORYBOARD_STYLE,
    image_aspect_ratio: (aspectRatio || '16:9').trim(),
  };
  if (Array.isArray(panels) && panels.length) {
    body.panels = panels
      .slice(0, body.panel_count)
      .map((p, i) => ({
        scene: p.scene ?? p.visual ?? '',
        narration: p.narration ?? p.text ?? '',
        panel_number: p.panel_number ?? p.segment_number ?? i + 1,
      }));
  }
  const { data } = await api.post('/narration/image-story/collage-prompt-preview', body);
  return typeof data?.prompt === 'string' ? data.prompt : '';
}

/** @deprecated Use buildImageGenerationPromptPreview */
export const buildCollagePromptPreview = buildImageGenerationPromptPreview;
